/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
/**
 * ID89 — Signpost add/edit form, extracted so it can be reused both in the
 * "Create Risk" stepper (step 1) and in the "+ Add Signpost" pop-up on the
 * merged watchlist risk details screen.
 *
 * All state lives in the parent (AnticipateEmergingRisk); this is a controlled
 * presentational component receiving values + setters via props.
 */
import { useState } from "react";
import { Grid, TextField, RadioGroup, Radio, FormControlLabel } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ResidualThresholdMatrixDialog from "./ResidualThresholdMatrixDialog";

export default function AddSignpostForm({
  spName,
  setSpName,
  spDesc,
  setSpDesc,
  signpostSubType,
  setSignpostSubType,
  signpostLibraryList,
  selectedLibraryEntry,
  setSelectedLibraryEntry,
  handleSelectSignPost,
  spThresholds,
  setSpThresholds,
  spTriggers,
  setSpTriggers,
  scenarioOptionLabels,
  findScenarioByLabel,
  scenarioAmount,
  scenarioToKey,
  riskId,
  onOpenFinder,
  onOpenSuggest,
  prioritisationMatrix,
}) {
  // ID54 — which trigger row's residual-threshold matrix picker is open.
  const [matrixIdx, setMatrixIdx] = useState(null);
  const setT = (i, patch) => {
    const u = [...spTriggers];
    u[i] = { ...u[i], ...patch };
    setSpTriggers(u);
  };
  return (
    <Grid container spacing={3} sx={{ pl: "8px" }}>
      {/* ── AI signpost finder (ID96) entry + bypass hint ── */}
      <Grid item xs={12}>
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            p: 1.5,
            borderRadius: 1,
            bgcolor: "rgba(82,0,155,0.05)",
            border: "1px solid rgba(82,0,155,0.15)",
          }}
        >
          <MDButton
            variant="contained"
            sx={{ bgcolor: "#52009b", color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
            disabled={!riskId}
            onClick={onOpenFinder}
          >
            ✨ Find the best available signposts
          </MDButton>
          {onOpenSuggest && (
            <MDButton
              variant="outlined"
              sx={{
                color: "#52009b",
                borderColor: "#52009b",
                "&:hover": { borderColor: "#3a0070" },
              }}
              disabled={!riskId}
              onClick={onOpenSuggest}
            >
              ✨ Suggest signposts
            </MDButton>
          )}
          <MDTypography variant="caption" color="text.secondary">
            {riskId
              ? "Let AI suggest signposts from our library (and the web) using your risk & company context — or set one up manually below / choose a Qualitative signal."
              : "Save the risk details first to enable the AI signpost finder. You can still add a signpost manually below."}
          </MDTypography>
        </MDBox>
      </Grid>
      {/* ── Name & description ── */}
      <Grid item xs={12}>
        <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
          Signpost Details
        </MDTypography>
        <MDBox display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Signpost Name *"
            sx={{ width: "280px" }}
            value={spName}
            onChange={(e) => setSpName(e.target.value)}
            size="small"
            required
            error={spName.trim() === ""}
            helperText={spName.trim() === "" ? "Name is required" : ""}
          />
          <TextField
            label="Description"
            sx={{ width: "400px" }}
            value={spDesc}
            onChange={(e) => setSpDesc(e.target.value)}
            size="small"
          />
        </MDBox>
      </Grid>

      {/* ── Sub-type selector ── */}
      <Grid item xs={12}>
        <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
          Signpost Type
        </MDTypography>
        <RadioGroup
          row
          value={signpostSubType}
          onChange={(e) => setSignpostSubType(e.target.value)}
        >
          <FormControlLabel
            value="quantitative"
            control={<Radio />}
            label="Quantitative (data feed + thresholds)"
          />
          <FormControlLabel
            value="qualitative"
            control={<Radio />}
            label="Qualitative (LLM trigger monitoring)"
          />
        </RadioGroup>
      </Grid>

      {/* ── Library picker (quantitative only — irrelevant for qualitative trigger signposts) ── */}
      {signpostSubType === "quantitative" && (
        <Grid item xs={12}>
          <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
            Signpost Library (optional — pre-configured data source)
          </MDTypography>
          <Autocomplete
            sx={{ width: "420px" }}
            options={signpostLibraryList.filter(
              (l) => l.isActive !== false && l.dataSourceType !== "llm_search"
            )}
            getOptionLabel={(o) => `${o.name} (${o.dataSourceType})`}
            value={selectedLibraryEntry}
            onChange={(_, v) => {
              setSelectedLibraryEntry(v);
              if (v) {
                if (!spName.trim()) setSpName(v.name);
                const typeMap = {
                  stock_yahoo: "1",
                  fx_alphavantage: "2",
                  excel_online: "4",
                  econdb: "5",
                  url_scrape: "4",
                  custom_api: "4",
                };
                handleSelectSignPost(null, v.dataSourceType, typeMap[v.dataSourceType] || "");
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Pick from library" variant="outlined" size="small" />
            )}
          />
          {selectedLibraryEntry && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Source: {selectedLibraryEntry.dataSourceType} · Unit:{" "}
              {selectedLibraryEntry.unit || "—"} · {selectedLibraryEntry.description}
            </MDTypography>
          )}
        </Grid>
      )}

      {/* ── Quantitative: threshold builder ── */}
      {signpostSubType === "quantitative" && (
        <Grid item xs={12}>
          <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
            Thresholds
          </MDTypography>
          {/* ID70 — the alert thresholds are USD movements that mirror the scenario values. Pick a
              Scenario on a row to copy its amount, or use "Populate from scenarios" below. The
              per-row description explains what the base/pessimistic/optimistic value represents. */}
          <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Thresholds are based on USD movements. Choosing a <strong>Scenario</strong> copies that
            scenario&apos;s amount into the threshold value; use the description to clarify what the
            base / pessimistic / optimistic value represents.
          </MDTypography>
          {spThresholds.map((t, i) => (
            <MDBox key={i} display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
              <TextField
                size="small"
                label="Value"
                type="number"
                sx={{ width: 110 }}
                value={t.value}
                onChange={(e) => {
                  const updated = [...spThresholds];
                  updated[i] = { ...updated[i], value: parseFloat(e.target.value) || 0 };
                  setSpThresholds(updated);
                }}
              />
              <Autocomplete
                size="small"
                sx={{ width: 130 }}
                value={t.direction}
                onChange={(_, v) => {
                  const u = [...spThresholds];
                  u[i] = { ...u[i], direction: v || "above" };
                  setSpThresholds(u);
                }}
                options={["above", "below"]}
                renderInput={(p) => <TextField {...p} label="Direction" />}
                disableClearable
              />
              <Autocomplete
                size="small"
                sx={{ width: 180 }}
                value={t.scenarioLabel || ""}
                onChange={(_, v) => {
                  const u = [...spThresholds];
                  const sc = findScenarioByLabel(v);
                  const amt = scenarioAmount(sc);
                  u[i] = {
                    ...u[i],
                    scenarioLabel: v || "",
                    scenario: sc ? scenarioToKey(sc) : u[i].scenario || "base",
                    value: amt != null ? amt : u[i].value,
                  };
                  setSpThresholds(u);
                }}
                options={scenarioOptionLabels}
                renderInput={(p) => <TextField {...p} label="Scenario" />}
                disableClearable
              />
              <TextField
                size="small"
                label="What it represents (%, USD, rating…)"
                sx={{ width: 220 }}
                value={t.description || ""}
                onChange={(e) => {
                  const u = [...spThresholds];
                  u[i] = { ...u[i], description: e.target.value };
                  setSpThresholds(u);
                }}
              />
              <MDButton
                size="small"
                variant="text"
                color="error"
                onClick={() => setSpThresholds(spThresholds.filter((_, j) => j !== i))}
              >
                Remove
              </MDButton>
            </MDBox>
          ))}
          <MDButton
            size="small"
            variant="outlined"
            sx={{ color: "#52009b", borderColor: "#52009b", mt: 1 }}
            onClick={() =>
              setSpThresholds([
                ...spThresholds,
                {
                  value: 0,
                  direction: "above",
                  scenario: "base",
                  scenarioLabel: "",
                  description: "",
                },
              ])
            }
          >
            + Add Threshold
          </MDButton>
          {/* ID70 — one-click: add a threshold per scenario that has a $ amount, seeded from it. */}
          <MDButton
            size="small"
            variant="text"
            color="info"
            sx={{ mt: 1, ml: 1, textTransform: "none" }}
            onClick={() => {
              const existing = new Set(spThresholds.map((t) => t.scenarioLabel).filter(Boolean));
              const additions = (scenarioOptionLabels || [])
                .filter((label) => !existing.has(label))
                .map((label) => {
                  const sc = findScenarioByLabel(label);
                  const amt = scenarioAmount(sc);
                  if (amt == null) return null;
                  return {
                    value: amt,
                    direction: "above",
                    scenario: sc ? scenarioToKey(sc) : "base",
                    scenarioLabel: label,
                    description: "",
                  };
                })
                .filter(Boolean);
              if (additions.length) setSpThresholds([...spThresholds, ...additions]);
            }}
          >
            Populate from scenarios
          </MDButton>
        </Grid>
      )}

      {/* ── Qualitative: trigger builder ── */}
      {signpostSubType === "qualitative" && (
        <Grid item xs={12}>
          <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
            Trigger Conditions
          </MDTypography>
          {spTriggers.map((t, i) => {
            const isResidual = t.triggerType === "residual_threshold";
            // ID96 — only ONE residual-risk threshold per signpost; extra triggers must be events.
            const residualElsewhere = spTriggers.some(
              (x, j) => j !== i && x.triggerType === "residual_threshold"
            );
            const typeOptions = residualElsewhere
              ? ["Trigger event"]
              : ["Trigger event", "Residual-risk threshold"];
            return (
              <MDBox
                key={i}
                sx={{
                  border: "1px solid rgba(82,0,155,0.2)",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <MDBox display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
                  <Autocomplete
                    size="small"
                    sx={{ width: 220 }}
                    value={isResidual ? "Residual-risk threshold" : "Trigger event"}
                    onChange={(_, v) =>
                      setT(i, {
                        triggerType:
                          v === "Residual-risk threshold" ? "residual_threshold" : "event",
                      })
                    }
                    options={typeOptions}
                    renderInput={(p) => <TextField {...p} label="Type" />}
                    disableClearable
                  />
                  <Autocomplete
                    size="small"
                    sx={{ width: 180 }}
                    value={t.scenarioLabel || ""}
                    onChange={(_, v) => {
                      const sc = findScenarioByLabel(v);
                      setT(i, {
                        scenarioLabel: v || "",
                        scenario: sc ? scenarioToKey(sc) : t.scenario || "base",
                      });
                    }}
                    options={scenarioOptionLabels}
                    renderInput={(p) => <TextField {...p} label="Scenario" />}
                    disableClearable
                  />
                  <MDBox flexGrow={1} />
                  <MDButton
                    size="small"
                    variant="text"
                    color="error"
                    onClick={() => setSpTriggers(spTriggers.filter((_, j) => j !== i))}
                  >
                    Remove
                  </MDButton>
                </MDBox>

                {isResidual ? (
                  /* ID54 — residual threshold: pick the alert level from the prioritisation matrix. */
                  <MDBox display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <MDButton
                      size="small"
                      variant="outlined"
                      sx={{ color: "#52009b", borderColor: "#52009b" }}
                      onClick={() => setMatrixIdx(i)}
                    >
                      {t.thresholdRating
                        ? `Alert level: ${t.thresholdRating} or worse`
                        : "Set alert level…"}
                    </MDButton>
                    <MDTypography variant="caption" color="text.secondary">
                      Alerts when the residual risk rating reaches this level or worse.
                    </MDTypography>
                  </MDBox>
                ) : (
                  /* Trigger event: just a free-text description. It is automatically monitored by a
                     strict hidden web/news scan (kept out of Horizon Scan); the detection spec is
                     generated behind the scenes, so nothing else needs to be configured here. */
                  <TextField
                    size="small"
                    label="Trigger event description"
                    placeholder="e.g. Another country formally joins the conflict"
                    multiline
                    fullWidth
                    value={t.description}
                    onChange={(e) => setT(i, { description: e.target.value })}
                  />
                )}

                <TextField
                  size="small"
                  label="Context Notes (opt)"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={t.contextNotes || ""}
                  onChange={(e) => setT(i, { contextNotes: e.target.value })}
                />
              </MDBox>
            );
          })}
          <MDButton
            size="small"
            variant="outlined"
            sx={{ color: "#52009b", borderColor: "#52009b", mt: 1 }}
            onClick={() =>
              setSpTriggers([
                ...spTriggers,
                {
                  description: "",
                  scenario: "base",
                  scenarioLabel: "",
                  contextNotes: "",
                  triggerType: "event",
                  thresholdCondition: "",
                  // Trigger events are always monitored by the strict hidden scan (default on).
                  monitorViaScan: true,
                },
              ])
            }
          >
            + Add Trigger
          </MDButton>

          <ResidualThresholdMatrixDialog
            open={matrixIdx != null}
            onClose={() => setMatrixIdx(null)}
            matrix={prioritisationMatrix}
            value={matrixIdx != null ? spTriggers[matrixIdx]?.thresholdRating : ""}
            onSelect={(rating) => {
              if (matrixIdx != null)
                setT(matrixIdx, {
                  thresholdRating: rating,
                  thresholdCondition: `Residual risk rating is ${rating} or worse`,
                  description: `Residual risk rating is ${rating} or worse`,
                });
            }}
          />
        </Grid>
      )}
    </Grid>
  );
}
